import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Plus, Car, Trash2, Pencil, FileText, ChevronDown, ChevronRight, X,
} from "lucide-react";
import localApi from "@/lib/localApi";
import { money, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const blankVeh = { year: "", make: "", model: "", vin: "", plate: "", nickname: "" };
const vehName = (v) => v.nickname || [v.year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle";

export function VehicleManager({ customer, onClose, onChanged }) {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(blankVeh);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [docs, setDocs] = useState({});
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await localApi.get(`/shop/customers/${customer.id}/vehicles`);
      setVehicles(data.vehicles || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customer.id]);

  const startAdd = () => { setEditingId(null); setForm(blankVeh); setAdding(true); };
  const startEdit = (v) => { setEditingId(v.id); setForm({ ...blankVeh, ...v }); setAdding(true); };

  const save = async () => {
    if (!form.make && !form.model && !form.vin && !form.nickname) return toast.error("Add a make/model, VIN or nickname");
    setSaving(true);
    try {
      if (editingId) { await localApi.put(`/shop/vehicles/${editingId}`, form); toast.success("Vehicle updated"); }
      else { await localApi.post(`/shop/customers/${customer.id}/vehicles`, form); toast.success("Vehicle added"); }
      setAdding(false); setForm(blankVeh); setEditingId(null);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save vehicle"); }
    finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Remove ${vehName(v)} and its linked paperwork records?`)) return;
    try { await localApi.delete(`/shop/vehicles/${v.id}`); await load(); onChanged && onChanged(); toast.success("Vehicle removed"); }
    catch { toast.error("Could not remove vehicle"); }
  };

  const toggleDocs = async (v) => {
    if (expanded === v.id) { setExpanded(null); return; }
    setExpanded(v.id);
    if (!docs[v.id]) {
      try { const { data } = await localApi.get(`/shop/vehicles/${v.id}/documents`); setDocs((d) => ({ ...d, [v.id]: data.documents || [] })); }
      catch { setDocs((d) => ({ ...d, [v.id]: [] })); }
    }
  };

  const newEstimate = (v) => {
    navigate("/invoices/new", {
      state: {
        prefill: {
          doc_type: "estimate",
          shopVehicleId: v.id, shopCustomerId: customer.id,
          customer_name: customer.name || "", customer_phone: customer.phone || "", customer_email: customer.email || "",
          vehicle_year: v.year || "", vehicle_make: v.make || "", vehicle_model: v.model || "", vehicle_vin: v.vin || "",
        },
      },
    });
  };

  const field = "h-9 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vehicle-manager">
        <DialogHeader>
          <DialogTitle className="text-slate-100 font-head flex items-center gap-2"><Car className="h-5 w-5 text-amber-400" /> {customer.name}'s Vehicles</DialogTitle>
          <DialogDescription className="text-slate-500">Add as many vehicles as you need. Each tracks its own estimates & paperwork.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
        ) : (
          <div className="space-y-3">
            {vehicles.length === 0 && !adding && (
              <p className="text-sm text-slate-500 text-center py-4" data-testid="no-vehicles">No vehicles yet. Add the customer's first vehicle below.</p>
            )}
            {vehicles.map((v) => (
              <div key={v.id} data-testid={`vehicle-${v.id}`} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => toggleDocs(v)} data-testid={`vehicle-toggle-${v.id}`} className="flex items-center gap-2 min-w-0 text-left">
                    {expanded === v.id ? <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-medium text-slate-100 truncate">{vehName(v)}</p>
                      <p className="text-[11px] text-slate-500 truncate">{[v.vin && `VIN ${v.vin}`, v.plate && `Plate ${v.plate}`].filter(Boolean).join(" · ") || "No VIN/plate"}</p>
                    </div>
                  </button>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" data-testid={`vehicle-estimate-${v.id}`} onClick={() => newEstimate(v)} className="h-7 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-xs"><FileText className="h-3.5 w-3.5 mr-1" /> Estimate</Button>
                    <Button variant="ghost" size="icon" data-testid={`vehicle-edit-${v.id}`} onClick={() => startEdit(v)} className="h-7 w-7 text-slate-500 hover:text-amber-400"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" data-testid={`vehicle-delete-${v.id}`} onClick={() => remove(v)} className="h-7 w-7 text-slate-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                {expanded === v.id && (
                  <div className="mt-3 pt-3 border-t border-slate-800" data-testid={`vehicle-docs-${v.id}`}>
                    {!docs[v.id] ? (
                      <Loader2 className="h-4 w-4 animate-spin text-slate-500 mx-auto" />
                    ) : docs[v.id].length === 0 ? (
                      <p className="text-xs text-slate-600">No estimates or invoices for this vehicle yet.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {docs[v.id].map((d) => (
                          <button key={d.id} data-testid={`vehicle-doc-${d.id}`} onClick={() => navigate(`/invoices/${d.invoice_id}`)}
                            className="w-full flex items-center justify-between text-left rounded-md px-2 py-1.5 hover:bg-slate-800/60">
                            <span className="text-xs text-slate-300 flex items-center gap-1.5"><FileText className="h-3 w-3 text-slate-500" /> {d.doc_type === "estimate" ? "Estimate" : "Invoice"} {d.number || ""} · {fmtDate(d.created_at)}</span>
                            <span className="text-xs font-semibold text-amber-400">{d.total != null ? money(d.total) : ""}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {adding ? (
              <div className="rounded-lg border border-amber-500/30 bg-slate-950/50 p-3 space-y-3" data-testid="vehicle-form">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{editingId ? "Edit vehicle" : "New vehicle"}</p>
                  <button onClick={() => { setAdding(false); setEditingId(null); }} className="text-slate-500 hover:text-slate-300"><X className="h-4 w-4" /></button>
                </div>
                <Input data-testid="veh-nickname" value={form.nickname} onChange={set("nickname")} placeholder="Nickname (e.g. Work Truck)" className={field} />
                <div className="grid grid-cols-3 gap-2">
                  <Input data-testid="veh-year" value={form.year} onChange={set("year")} placeholder="Year" className={field} />
                  <Input data-testid="veh-make" value={form.make} onChange={set("make")} placeholder="Make" className={field} />
                  <Input data-testid="veh-model" value={form.model} onChange={set("model")} placeholder="Model" className={field} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="veh-vin" value={form.vin} onChange={set("vin")} placeholder="VIN" className={field} />
                  <Input data-testid="veh-plate" value={form.plate} onChange={set("plate")} placeholder="License plate" className={field} />
                </div>
                <Button data-testid="veh-save" onClick={save} disabled={saving} className="w-full h-9 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                  {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} {editingId ? "Save changes" : "Add vehicle"}
                </Button>
              </div>
            ) : (
              <Button variant="outline" data-testid="add-vehicle-button" onClick={startAdd} className="w-full bg-slate-900 border-slate-700 border-dashed text-slate-300 hover:bg-slate-800">
                <Plus className="h-4 w-4 mr-1.5" /> Add vehicle
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
